import React from 'react';
import { Helmet } from 'react-helmet-async';

export interface BlogSEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  publishDate?: string;
  modifiedDate?: string;
  author?: string;
  category?: string;
  tags?: string[];
}

const BlogSEO: React.FC<BlogSEOProps> = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle = title,
  ogDescription = description,
  ogImage = "https://afsprakenmaker.be/lovable-uploads/ac401e9b-2809-41cc-9a40-cdd109a1f836.png",
  publishDate,
  modifiedDate,
  author = "Afsprakenmaker Team",
  category = "B2B Sales",
  tags = []
}) => {
  const fullCanonicalUrl = canonicalUrl ? 
    (canonicalUrl.startsWith('http') ? canonicalUrl : `https://afsprakenmaker.be${canonicalUrl}`) : 
    undefined;

  // Create article structured data - enhanced with more detailed data and text justification info
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": ogImage,
    "author": {
      "@type": "Person",
      "name": author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Afsprakenmaker.be",
      "logo": {
        "@type": "ImageObject",
        "url": "https://afsprakenmaker.be/lovable-uploads/43aed143-9c8c-4ba6-8e3d-43007bdbe014.png"
      }
    },
    "datePublished": publishDate,
    "dateModified": modifiedDate || publishDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": fullCanonicalUrl
    },
    "keywords": tags.join(", "),
    "articleBody": description,
    "articleSection": category,
    "isAccessibleForFree": "True"
  };

  // Create breadcrumb structured data
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://afsprakenmaker.be"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://afsprakenmaker.be/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": title,
        "item": fullCanonicalUrl
      }
    ]
  };

  // Add article style structured data for text justification
  const articleStyleStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["article", ".article-content"]
    },
    "mainContentOfPage": {
      "@type": "WebPageElement",
      "cssSelector": "article"
    }
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {fullCanonicalUrl && <link rel="canonical" href={fullCanonicalUrl} />}
      
      {/* OpenGraph Tags (for social sharing) */}
      <meta property="og:type" content="article" />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      {fullCanonicalUrl && <meta property="og:url" content={fullCanonicalUrl} />}
      <meta property="og:image" content={ogImage} />
      <meta property="article:published_time" content={publishDate} />
      {modifiedDate && <meta property="article:modified_time" content={modifiedDate} />}
      <meta property="article:section" content={category} />
      {tags.map((tag, index) => (
        <meta key={index} property="article:tag" content={tag} />
      ))}
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={ogDescription} />
      <meta name="twitter:image" content={ogImage} />
      
      {/* Enhanced Meta Tags for SEO */}
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <meta name="article:published_time" content={publishDate} />
      {modifiedDate && <meta name="article:modified_time" content={modifiedDate} />}
      
      {/* Additional readability meta tags */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="theme-color" content="#D3E4FD" />
      <meta name="msapplication-TileColor" content="#D3E4FD" />
      
      {/* Blog Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(articleStructuredData)}
      </script>

      {/* Breadcrumb Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbStructuredData)}
      </script>
      
      {/* Article Style Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(articleStyleStructuredData)}
      </script>
    </Helmet>
  );
};

export default BlogSEO;
